// Convert a MediaRecorder capture (webm/opus in Chrome, mp4/aac in Safari)
// to 16kHz mono 16-bit WAV, base64-encoded for the /ai/transcribe route.
//
// Why WAV: Azure — the ONLY provider behind microsoft/mai-transcribe-2 —
// rejects webm/opus with 400 "Provider returned 400", while every OpenRouter
// STT model accepts WAV (verified 2026-10-09 with a real 1.5s Chrome capture:
// mai-transcribe-2 → 400; whisper-1, whisper-large-v3, gpt-4o-transcribe,
// elevenlabs/scribe-v2, google/gemini-3.5-transcribe → 200 on the same
// payload once WAV). The settings-page transcribe-test passes because it
// sends a programmatically built WAV; dictation failed because it sent the
// raw webm. This module makes the dictation path send WAV too.
//
// 16kHz mono 16-bit = 32KB/s — the standard STT rate; keeps payloads small
// (2 min ≈ 3.8MB raw / 5.1MB base64).
//
// 0020: the dictation format is now user-selectable (wav | flac | mp3 | aac |
// webm). The decode+resample+int16 pipeline below is shared by the WAV, MP3
// and FLAC encoders via the exported decodeToMonoPcm16() helper.

const TARGET_SAMPLE_RATE = 16000;

export interface MonoPcm16 {
  samples: Int16Array; // 16kHz mono 16-bit PCM
  sampleRate: 16000;
  duration: number; // seconds
}

/**
 * Decode whatever the browser recorded (webm/opus, mp4/aac, wav…) into
 * 16kHz mono 16-bit PCM — the shared front-end for the WAV / MP3 / FLAC
 * encoders. decodeAudioData handles the browser's own capture format;
 * resampling to 16kHz mono goes through an OfflineAudioContext render.
 *
 * Throws Error("Could not convert recording") when the blob can't be
 * decoded (empty/corrupt recording).
 */
export async function decodeToMonoPcm16(blob: Blob): Promise<MonoPcm16> {
  const arrayBuffer = await blob.arrayBuffer();

  const ctx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(arrayBuffer);
  } catch {
    throw new Error("Could not convert recording");
  } finally {
    void ctx.close();
  }

  // Resample to 16kHz mono by rendering through an OfflineAudioContext.
  // length must be >= 1 or the constructor throws on empty recordings.
  const frames = Math.max(1, Math.ceil(decoded.duration * TARGET_SAMPLE_RATE));
  const offline = new OfflineAudioContext(1, frames, TARGET_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();

  // Float32 [-1,1] → clamped int16.
  const channelData = rendered.getChannelData(0);
  const samples = new Int16Array(channelData.length);
  for (let i = 0; i < channelData.length; i++) {
    const s = Math.max(-1, Math.min(1, channelData[i]));
    samples[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }

  return { samples, sampleRate: TARGET_SAMPLE_RATE, duration: rendered.duration };
}

/**
 * Encode 16kHz mono 16-bit PCM as a RIFF/WAV file: 44-byte header
 * (RIFF/WAVE/fmt/data). Same layout as the backend's testTranscribe
 * silence-WAV builder in apps/api/src/lib/openrouter.ts.
 */
function encodeWavFromPcm16(samples: Int16Array, sampleRate: number): ArrayBuffer {
  const dataSize = samples.length * 2; // 16-bit mono
  const out = new ArrayBuffer(44 + dataSize);
  const view = new DataView(out);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true); // rest of file size
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true); // 16000
  view.setUint32(28, sampleRate * 2, true); // byte rate = 32000
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);
  new Uint8Array(out, 44).set(new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength));
  return out;
}

/**
 * Decode whatever the browser recorded, resample to 16kHz mono, encode as
 * WAV, and return it base64-encoded (no data: prefix) with format "wav".
 *
 * Uses a plain AudioContext for decodeAudioData — the dictation flow always
 * starts from a user click (mic button), so the autoplay policy is
 * satisfied. decodeAudioData handles the browser's own capture format
 * (Chrome: webm/opus, Safari: mp4/aac).
 *
 * Throws Error("Could not convert recording") when the blob can't be
 * decoded (empty/corrupt recording).
 */
export async function audioBlobToWavBase64(blob: Blob): Promise<{ base64: string; format: string }> {
  const { samples, sampleRate } = await decodeToMonoPcm16(blob);
  const wavBuffer = encodeWavFromPcm16(samples, sampleRate);

  // ArrayBuffer → base64 (chunk to avoid String.fromCharCode arg limits —
  // same pattern as the backend's WAV builder).
  const bytes = new Uint8Array(wavBuffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return { base64: btoa(binary), format: "wav" };
}
